import {defineStore} from 'pinia';
import {computed, ref} from 'vue';
import {useApiClient} from "@/hooks/api-hook.ts";
import {
  AircraftType,
  AircraftTypeCreateRequest,
  AircraftTypeUpdateRequest
} from "@/api/client/models/index.ts";

export const useCharterAircraftTypesStore = defineStore('charter-aircraft-types', () => {
  const api = useApiClient();

  // State
  const aircraftTypes = ref<AircraftType[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);

  // Getters
  const aircraftTypesCount = computed(() => aircraftTypes.value.length);
  const hasAircraftTypes = computed(() => aircraftTypes.value.length > 0);
  const isLoading = computed(() => loading.value);
  const hasError = computed(() => error.value !== null);

  // Actions
  async function fetchAircraftTypes(isActive: boolean = true) {
    loading.value = true;
    error.value = null;

    try {
      aircraftTypes.value = await api.aircraftType.get({queryParameters: {isActive}}) ?? [];
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to fetch aircraft types';
      console.error('Error fetching aircraft types:', err);
    } finally {
      loading.value = false;
    }
  }

  function clearAircraftTypes() {
    aircraftTypes.value = [];
    error.value = null;
  }

  function clearError() {
    error.value = null;
  }

  async function addAircraftType(aircraftTypeCreateRequest: AircraftTypeCreateRequest) {
    loading.value = true;
    error.value = null;

    try {
      const newAircraftType = await api.aircraftType.post(aircraftTypeCreateRequest);
      // Refresh the aircraft types list after adding a new aircraft type
      await fetchAircraftTypes();
      return newAircraftType;
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to add aircraft type';
      console.error('Error adding aircraft type:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function updateAircraftType(aircraftTypeUpdateRequest: AircraftTypeUpdateRequest) {
    loading.value = true;
    error.value = null;

    try {
      await api.aircraftType.put(aircraftTypeUpdateRequest);
      // Refresh the aircraft types list after updating an aircraft type
      await fetchAircraftTypes();
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to update aircraft type';
      console.error('Error updating aircraft type:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function toggleAircraftTypeActive(id: number, isActive: boolean) {
    loading.value = true;
    error.value = null;

    try {
      await api.aircraftType.byId(id).byIsActive(isActive).post()
      // Refresh the aircraft types list after toggling aircraft type active status
      await fetchAircraftTypes();
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to toggle aircraft type active status';
      console.error('Error toggling aircraft type active status:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  return {
    // State
    aircraftTypes,
    loading,
    error,

    // Getters
    aircraftTypesCount,
    hasAircraftTypes,
    isLoading,
    hasError,

    // Actions
    fetchAircraftTypes,
    clearAircraftTypes,
    clearError,
    addAircraftType,
    updateAircraftType,
    toggleAircraftTypeActive
  };
});
